import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  // MERGE (IfrahWorks) — Tooltip is hers, for the API-key buttons.
  // Chip is intentionally absent: it moved into SubscriptionSummary with the
  // plan block (4.1), so importing it here would be an unused import.
  Tooltip,
  Link
} from '@mui/material';
import {
  CreditCard as CardIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Visibility,
  VisibilityOff,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  VpnKey as KeyIcon,
  Payment as PaymentIcon,
  HomeWork as HomeIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import api from '../services/api';
import SubscriptionSummary from './SubscriptionSummary';
// The SAME modal the sign-up screen uses, so the documents a user agreed to at
// registration are the documents they can re-read here — one source, no second
// copy of the policy text to drift.
import LegalModal, { PRIVACY_SECTIONS } from './LegalModal';
import { personNameFrom, joinPersonName, validatePersonName } from '../utils/personName';
import {
  signInProvider, passwordSecurityState, validatePasswordForm,
} from '../utils/passwordSecurity';

// ── Design tokens ────────────────────────────────────────────────────────────
const TEAL          = '#1C7C73';
const TEAL_LIGHT    = '#E8F3F1';
/**
 * Where account deletion is actually handled, until there is an endpoint for it.
 *
 * Named rather than inlined because it appears twice in the dialog — as the
 * address someone reads and as the mailto they press — and a support address
 * that differs between the two is worse than one that is merely hard-coded.
 * Note this is NOT the address in LegalModal (support@improveseoplugin.com);
 * they were already different before this change and only support can say which
 * is right.
 */
const SUPPORT_EMAIL = 'support@improveseo.com';

const ORANGE        = '#E55432';
const ORANGE_GRAD   = 'linear-gradient(135deg, #BF3A1A 0%, #E86340 100%)';
const TEAL_GRAD     = 'linear-gradient(135deg, #0E6059 0%, #1C7C73 100%)';
const CARD_SHADOW   = '0px 4px 4px 0px rgba(0,0,0,0.25)';
const TEXT          = '#1A1A1A';
const TEXT_MUTED    = '#8A8F85';

const CARD = {
  background: '#FFFFFF',
  borderRadius: '20px',
  boxShadow: CARD_SHADOW,
  p: '32px',
  mb: 3,
};

const LABEL_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: TEXT_MUTED,
  mb: 0.75,
};

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: '#F5F6F0',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: '#D0D0D0' },
    '&.Mui-focused fieldset': { borderColor: TEAL },
    '&.Mui-disabled': { background: '#ECECEC' },
  },
  '& .MuiOutlinedInput-input': {
    fontFamily: 'Inter, sans-serif',
    color: TEXT,
  },
};

const IconBox = ({ bg, children }) => (
  <Box sx={{
    width: 48, height: 48, borderRadius: '12px', background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }}>
    {children}
  </Box>
);

/**
 * How a saved card was presented, when it came through a wallet.
 *
 * Stripe reports this as `card.wallet.type`. It is NOT a separate kind of
 * payment method: Apple Pay and Google Pay both save the underlying card, which
 * is what makes them chargeable off-session for renewals. Only the label
 * differs, so this is presentation only.
 *
 * Unlisted or null → no tag, which is the correct outcome for a card typed in
 * by hand.
 */
const WALLET_LABELS = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  link: 'Link',
  samsung_pay: 'Samsung Pay',
};

const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
    {icon}
    <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 18, color: TEXT }}>
      {title}
    </Typography>
  </Box>
);

// ── Component ────────────────────────────────────────────────────────────────
const Settings = () => {
  const { user, userData, refreshUserData } = useAuth();
  const [loading, setLoading]               = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [copied, setCopied]                 = useState(false);
  // 4.1 — bumped after anything that can change billing (card added/removed,
  // default changed) so the plan block refetches instead of showing stale data.
  // Plan changes themselves happen on the Credits tab, which remounts this one.
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);
  // 4.2 — null while unknown, so the "not on file" marker never flashes before
  // the answer arrives.
  const [hasAddress, setHasAddress] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [addressError, setAddressError] = useState(null);
  // Which legal document the header links have opened, if any. Same shape the
  // sign-up screen uses.
  const [legalModal, setLegalModal] = useState({ open: false, type: 'privacy' });
  const invalidateSubscription = () => setSubscriptionRefreshKey((k) => k + 1);

  // ── What this card asks for ─────────────────────────────────────────────
  // OLD: Keyed on has_password (the API's !!users.password_hash), NOT on how the
  // (password_hash alone missed every web email signup — see passwordSecurity.js.)
  // Keyed on has_password (our password_hash OR Supabase Auth), NOT on how the
  // account signed up. The previous test — oauth_provider && !has_password —
  // read false for every WEB Google sign-up, because that column is only
  // written by the plugin's OAuth path; a Supabase sign-in never sets it. Those
  // users were shown a Current Password field they could not possibly fill,
  // which is the reported bug. See utils/passwordSecurity.js.
  const provider = signInProvider(user, userData);
  // ── A password set HERE counts at once, whatever userData still says ──────
  // After a Google account set its first password the card stayed on "Set a
  // Password" (2 fields) for the rest of the session: nothing refetched
  // has_password after the set, and the one refetch that did happen — the
  // USER_UPDATED listener in AuthContext — fires as updateUser resolves, BEFORE
  // api.setPassword writes password_hash, so a server reading that column
  // answered false. It also meant the new password could be changed again
  // without the current one. Holding the Supabase user id that just set a
  // password makes the card flip to Update Security immediately, and no stale
  // or out-of-order /users/by-email reply can flip it back.
  const [passwordSetFor, setPasswordSetFor] = useState(null);
  const security = passwordSecurityState({
    // OLD: hasPassword: userData?.has_password,
    // (Stale for the rest of the session once a password was set here — see above.)
    hasPassword: passwordSetFor !== null && passwordSetFor === user?.id
      ? true
      : userData?.has_password,
    provider,
  });

  // MERGE (IfrahWorks) — the plan-name mapping and trial-state derivation that
  // lived here have MOVED INTO SubscriptionSummary, which now owns the whole
  // plan block. Kept here as a pointer so the logic is findable from where it
  // used to be:
  //
  //   const planSlug = (userData?.plan_slug || '').toLowerCase();
  //   const onFreePlan = !planSlug || planSlug === 'free';
  //   const trialExpired = userData?.trial_expired === true
  //     || (onFreePlan && !!userData?.trial_ends_at && new Date(userData.trial_ends_at).getTime() < Date.now());
  //   const planLabel = onFreePlan
  //     ? (trialExpired ? 'Trial ended' : 'Free Trial')
  //     : getPlanDisplayName({ name: userData?.plan_name, slug: userData?.plan_slug });
  //
  // Neither behaviour was dropped: paid plans still render through
  // getPlanDisplayName, and a free-plan account still shows Free Trial /
  // Trial ended with the orange tint once expired.

  // Profile edit — first and last name are collected SEPARATELY.
  //
  // `nameDraft` is null until the user types, so the fields keep showing the
  // stored name while userData is still loading and a slow profile fetch cannot
  // overwrite what someone has already typed.
  // OLD: const [editingName, setEditingName] = useState(false);
  //      const [newName, setNewName] = useState('');
  const [nameDraft, setNameDraft] = useState(null); // { first, last } | null
  const [nameLoading, setNameLoading] = useState(false);

  // What is on file, what is in the fields, and whether they differ. Derived
  // rather than stored, so "has this been edited?" cannot get out of step with
  // the inputs — the old `editingName` flag latched true on the first keystroke
  // and stayed true even if the text was typed back to its original value.
  const storedName = personNameFrom(userData);
  const nameValue = nameDraft || storedName;
  const nameDirty = nameValue.first !== storedName.first || nameValue.last !== storedName.last;
  const setNamePart = (part) => (e) => setNameDraft({ ...nameValue, [part]: e.target.value });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword]         = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading]         = useState(false);

  // API key regen
  const [regenKeyDialogOpen, setRegenKeyDialogOpen] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [regenKeyLoading, setRegenKeyLoading]       = useState(false);

  // Dialogs
  const [confirmDialogOpen, setConfirmDialogOpen]           = useState(false);
  const [cardToRemove, setCardToRemove]                     = useState(null);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen]               = useState(false);
  const [errorDialogMessage, setErrorDialogMessage]         = useState('');
  const [successDialogOpen, setSuccessDialogOpen]           = useState(false);
  const [successDialogMessage, setSuccessDialogMessage]     = useState('');

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (userData?.id) fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // 4.2 — one cheap read to drive the "not on file" marker. Failure leaves it
  // null, so a network blip never falsely tells the user their address is
  // missing.
  useEffect(() => {
    if (!userData?.id) return;
    let active = true;
    api.getUserAddress(userData.id)
      .then((res) => {
        if (!active) return;
        // Unknown when storage is unavailable (migration 003 pending), which
        // reads the same as "no address yet": the card shows no ON FILE badge
        // and tells the user Stripe will ask at their next purchase. That is
        // true either way now, so an unreachable address store no longer needs
        // a third state to avoid promising something it cannot deliver.
        const d = res?.data || {};
        setHasAddress(d.available === false ? null : !!d.has_address);
      })
      .catch(() => { /* leave unknown */ });
    return () => { active = false; };
  }, [userData?.id]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const result = await api.getPaymentMethods(userData.id);
      setPaymentMethods(result.payment_methods || []);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setErrorDialogMessage('Failed to load payment methods. Please refresh the page.');
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async () => {
    try {
      setLoading(true);
      const result = await api.createSetupSession(userData.id);
      if (result.url) window.location.href = result.url;
    } catch (err) {
      console.error('Error creating setup session:', err);
      setErrorDialogMessage(err.response?.data?.error || 'Failed to create payment setup. Please try again.');
      setErrorDialogOpen(true);
      setLoading(false);
    }
  };

  const handleRemoveCard = (paymentMethodId) => {
    setCardToRemove(paymentMethodId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmRemoveCard = async () => {
    if (!cardToRemove) return;
    try {
      setLoading(true);
      setConfirmDialogOpen(false);
      await api.removePaymentMethod(userData.id, cardToRemove);
      setSuccessDialogMessage('Card removed successfully!');
      setSuccessDialogOpen(true);
      await fetchPaymentMethods();
    } catch (err) {
      console.error('Error removing card:', err);
      setErrorDialogMessage(err.response?.data?.error || 'Failed to remove card. Please try again.');
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
      setCardToRemove(null);
    }
  };

  const handleCancelRemoveCard = () => {
    setConfirmDialogOpen(false);
    setCardToRemove(null);
  };

  const handleSetDefaultCard = async (paymentMethodId) => {
    try {
      setLoading(true);
      await api.setDefaultPaymentMethod(userData.id, paymentMethodId);
      setSuccessDialogMessage('Default card updated successfully!');
      setSuccessDialogOpen(true);
      await fetchPaymentMethods();
      invalidateSubscription(); // 4.1 — billing changed; don't show stale plan data
    } catch (err) {
      console.error('Error setting default card:', err);
      setErrorDialogMessage(err.response?.data?.error || 'Failed to set default card. Please try again.');
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveName = async () => {
    const invalid = validatePersonName(nameValue);
    if (invalid) {
      setErrorDialogMessage(invalid);
      setErrorDialogOpen(true);
      return;
    }
    const first = nameValue.first.trim();
    const last = nameValue.last.trim();
    try {
      setNameLoading(true);
      // The parts AND the joined string. The parts are what the user actually
      // gave us; `name` is what the account record has always stored and what
      // the Dashboard greeting, header chip and avatar initials read. Sending
      // both means the split works the moment the API keeps the columns, and
      // nothing regresses in the meantime.
      await api.updateProfile(userData.id, {
        first_name: first,
        last_name: last,
        name: joinPersonName(first, last),
      });
      await refreshUserData();
      setNameDraft(null); // fall back to the freshly stored values
      setSuccessDialogMessage('Name updated successfully!');
      setSuccessDialogOpen(true);
    } catch (err) {
      setErrorDialogMessage(err.response?.data?.error || 'Failed to update name.');
      setErrorDialogOpen(true);
    } finally {
      setNameLoading(false);
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    // Every rule in one place, so the form and its tests cannot disagree about
    // what is acceptable. See utils/passwordSecurity.js.
    const problem = validatePasswordForm({
      currentPassword,
      newPassword,
      confirmPassword,
      requiresCurrentPassword: security.requiresCurrentPassword,
    });
    if (problem) {
      setErrorDialogMessage(problem);
      setErrorDialogOpen(true);
      return;
    }

    try {
      setPasswordLoading(true);

      // ── The current password is now actually CHECKED ────────────────────
      // It was collected and then ignored: the handler went straight to
      // updateUser, so the field was decorative and anyone with an open
      // session — a shared laptop, a borrowed phone — could change the
      // password without knowing the old one.
      //
      // Verified by re-authenticating with it, which is the only way to prove
      // it against the identity provider that actually holds it. Supabase
      // returns the SAME session on success, so this does not disturb the one
      // the user is already in.
      //
      // Skipped when there is no password to check (the Google case): there is
      // nothing to prove, and the field is not shown.
      if (security.requiresCurrentPassword) {
        const { error: reauthError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });
        if (reauthError) {
          // Deliberately not the provider's wording, which leaks whether the
          // ACCOUNT exists as well as whether the password is right.
          setErrorDialogMessage('Your current password is not correct.');
          setErrorDialogOpen(true);
          return;
        }
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      // Supabase holds the password from this moment, so the card must now ask
      // for it. See passwordSetFor above.
      setPasswordSetFor(user.id);
      // OLD: // Keeps our own password_hash in step, which is what has_password reads
      // OLD: // and therefore what this card shows next time it loads.
      // OLD: await api.setPassword(user.email, newPassword);
      // (A failed sync reported "Failed to change password" for a password that
      // HAD changed, and left the card offering to set one without the current.)
      //
      // Keeps our own password_hash in step. Best-effort, like UpdatePassword.jsx:
      // the password has already changed, and the API's has_password also reads
      // Supabase Auth, so a missed sync no longer misreports the account.
      //
      // It is also what sends the confirmation email, so it says which event
      // this was: 'set' for an account that had no password (Scenario 2),
      // 'changed' otherwise (Scenarios 1 and 3).
      try {
        // OLD: await api.setPassword(user.email, newPassword);
        await api.setPassword(user.email, newPassword, security.isFirstTimeSet ? 'set' : 'changed');
      } catch (syncError) {
        // Never the password — only that the sync failed.
        console.error('Could not sync the new password to the account record.');
      }
      // Re-read the account now that password_hash is written, so the rest of
      // userData is fresh too. Best-effort for the same reason.
      try {
        await refreshUserData();
      } catch (refreshError) {
        console.error('Could not refresh account data after the password change.');
      }
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccessDialogMessage(
        security.isFirstTimeSet
          ? 'Password set. You can now sign in with your email address as well.'
          : 'Password updated.'
      );
      setSuccessDialogOpen(true);
    } catch (err) {
      // err.message only — never the form values.
      setErrorDialogMessage(err.message || 'Failed to change password.');
      setErrorDialogOpen(true);
    } finally {
      setPasswordLoading(false);
    }
  };

  /**
   * Send the user to Stripe to manage their billing details.
   *
   * A full-page redirect, not a new tab: the portal's own return link brings
   * them back here, and a tab would leave them with two copies of the app and
   * an address edited in only one of them.
   */
  const handleOpenBillingPortal = async () => {
    try {
      setPortalLoading(true);
      setAddressError(null);
      const { url } = await api.openBillingPortal(userData.id, '/settings');
      if (!url) throw new Error('No portal link came back.');
      window.location.href = url;
    } catch (err) {
      // Left on-screen rather than raised in a dialog: the button is still
      // right there to try again, and interrupting with a modal is the thing
      // this whole change set out to remove.
      setAddressError(
        err.response?.data?.error || 'Could not open the billing portal. Please try again.',
      );
      setPortalLoading(false);
    }
  };

  const handleRegenerateApiKey = async () => {
    try {
      setRegenKeyLoading(true);
      setRegenKeyDialogOpen(false);
      await api.regenerateApiKey(userData.id);
      await refreshUserData();
      setSuccessDialogMessage('API key regenerated! Update your WordPress plugins with the new key.');
      setSuccessDialogOpen(true);
    } catch (err) {
      setErrorDialogMessage(err.response?.data?.error || 'Failed to regenerate API key.');
      setErrorDialogOpen(true);
    } finally {
      setRegenKeyLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pb: 4 }}>

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <Typography sx={{
        fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 32, color: TEXT, mb: 1,
      }}>
        Account Settings
      </Typography>
      <Typography sx={{
        fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_MUTED, mb: 1.5, maxWidth: 640,
      }}>
        Manage your personal information, security preferences, and integration configurations
        for the ImproveSEO ecosystem.
      </Typography>

      {/* ── Data use + legal documents ───────────────────────────────────────
          The policies were reachable only from the sign-up screen, so a signed-in
          user had no way back to the documents they had agreed to. This states
          plainly what the page collects and why, and links the SAME two
          documents the sign-up screen shows — one source, so there is no second
          copy of the policy text to drift.

          Kept to a single line directly under the header, per the request: it is
          a reference, not a banner, and should not push the settings cards down
          the page. */}
      <Typography sx={{
        fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED, mb: 4, maxWidth: 640, lineHeight: '19px',
      }}>
        We use your name, email address, billing address, and payment methods to run your
        account, process payments, and calculate tax. See our{' '}
        <Link
          component="button"
          type="button"
          /* Opens AT the data-use section rather than the top of the policy —
             the same treatment the billing-address form uses, and the reason is
             the same: this sentence is a claim about how data is used, so it
             should land on the text that backs it. */
          onClick={() => setLegalModal({ open: true, type: 'privacy', anchor: PRIVACY_SECTIONS.USAGE_PURPOSE })}
          sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: TEAL, fontWeight: 700, verticalAlign: 'baseline' }}
        >
          Privacy Policy
        </Link>
        {' '}and{' '}
        <Link
          component="button"
          type="button"
          onClick={() => setLegalModal({ open: true, type: 'terms' })}
          sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: TEAL, fontWeight: 700, verticalAlign: 'baseline' }}
        >
          Terms of Service
        </Link>
        .
      </Typography>

      <LegalModal
        open={legalModal.open}
        onClose={() => setLegalModal((m) => ({ ...m, open: false }))}
        type={legalModal.type}
        anchor={legalModal.anchor || null}
      />

      {/* ── Row 1: Profile + Security ────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 3 }}>

        {/* Profile Information */}
        <Box sx={CARD}>
          <SectionHeader
            title="Profile Information"
            icon={
              <IconBox bg={TEAL_LIGHT}>
                <PersonIcon sx={{ color: TEAL, fontSize: 24 }} />
              </IconBox>
            }
          />

          {/* The key to the asterisks, placed before the first one is met. */}
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_MUTED, mb: 1.5 }}>
            * Required
          </Typography>

          {/* First and last name are collected separately.
              OLD: a single "Full Name" field, which left us guessing at the
              parts every time one was needed — a salutation, an invoice, a
              sorted list. */}
          {/* Two name fields side by side need a single column on a phone —
              the card itself is already full width there. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
            <Box>
              <Typography sx={LABEL_SX}>First Name *</Typography>
              <TextField
                fullWidth size="small"
                value={nameValue.first}
                onChange={setNamePart('first')}
                placeholder="Enter your first name"
                inputProps={{ maxLength: 100, 'aria-label': 'First name' }}
                sx={INPUT_SX}
              />
            </Box>
            <Box>
              <Typography sx={LABEL_SX}>Last Name *</Typography>
              <TextField
                fullWidth size="small"
                value={nameValue.last}
                onChange={setNamePart('last')}
                placeholder="Enter your last name"
                inputProps={{ maxLength: 100, 'aria-label': 'Last name' }}
                sx={INPUT_SX}
              />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={LABEL_SX}>Email Address</Typography>
              <TextField
                fullWidth size="small"
                value={user?.email || ''}
                InputProps={{ readOnly: true }}
                sx={INPUT_SX}
              />
            </Box>
          </Box>

          {/* 4.1 — real subscription data (price, status, renewal/end date,
              scheduled change, included credits) with proper loading, error,
              free-tier and cancelling states.

              OLD — a lone chip. The NAME was dynamic, but nothing else about
              the subscription was shown, so a cancelled plan was
              indistinguishable from an active one, and the "Basic Plan" fallback
              was displayed for any user whose plan_name failed to resolve:

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Typography ...>Current Plan:</Typography>
                <Chip label={userData?.plan_name || 'Basic Plan'} ... />
                <Typography ...>Member since: ...</Typography>
              </Box> */}
          <SubscriptionSummary userId={userData?.id} userData={userData} refreshKey={subscriptionRefreshKey} />
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_MUTED }}>
              Member since:{' '}
              {userData?.created_at
                ? new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(new Date(userData.created_at))
                : '—'}
            </Typography>
          </Box>

          <Button
            onClick={handleSaveName}
            disabled={nameLoading || !nameDirty}
            sx={{
              background: nameDirty ? TEAL_GRAD : '#E0E0E0',
              color: nameDirty ? '#fff' : '#999',
              borderRadius: '10px',
              fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14,
              px: 3, py: 1.25, textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { background: nameDirty ? TEAL_GRAD : '#E0E0E0', boxShadow: 'none' },
              '&:disabled': { background: '#E0E0E0', color: '#999' },
            }}
          >
            {nameLoading && <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />}
            Save changes
          </Button>
        </Box>

        {/* Update Security */}
        <Box sx={CARD}>
          <SectionHeader
            title={security.title}
            icon={
              <IconBox bg="#FDE8E4">
                <LockIcon sx={{ color: ORANGE, fontSize: 24 }} />
              </IconBox>
            }
          />

          {/* The wording is derived, not written here, so it can name the actual
              provider and stay in step with the button beside it. */}
          {security.notice && (
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED, mb: 2.5 }}>
              {security.notice}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {security.requiresCurrentPassword && (
              <Box>
                <Typography sx={LABEL_SX}>Current Password</Typography>
                <TextField
                  fullWidth size="small"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowCurrentPassword(v => !v)} edge="end">
                          {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={INPUT_SX}
                />
              </Box>
            )}

            <Box>
              <Typography sx={LABEL_SX}>New Password</Typography>
              <TextField
                fullWidth size="small"
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                placeholder="Min 8 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNewPassword(v => !v)} edge="end">
                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={INPUT_SX}
              />
            </Box>

            <Box>
              <Typography sx={LABEL_SX}>Confirm New Password</Typography>
              <TextField
                fullWidth size="small"
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowConfirmPassword(v => !v)} edge="end">
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={INPUT_SX}
              />
            </Box>
          </Box>

          <Button
            onClick={handleChangePassword}
            disabled={passwordLoading || !passwordForm.newPassword || !passwordForm.confirmPassword}
            sx={{
              background: ORANGE_GRAD, color: '#fff', borderRadius: '10px',
              fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14,
              px: 3, py: 1.25, textTransform: 'none', boxShadow: 'none',
              '&:hover': { background: ORANGE_GRAD, opacity: 0.9, boxShadow: 'none' },
              '&:disabled': { background: '#E0E0E0', color: '#999' },
            }}
          >
            {passwordLoading && <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />}
            {security.buttonLabel}
          </Button>
        </Box>
      </Box>

      {/* ── API Configuration ─────────────────────────────────────────────── */}
      <Box sx={CARD}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconBox bg="#EEE8F9">
              <KeyIcon sx={{ color: '#7B4FBF', fontSize: 24 }} />
            </IconBox>
            <Box>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 18, color: TEXT }}>
                API Configuration
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED }}>
                Below API Key connects your ImproveSEO user account with your WordPress website.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Copy to clipboard">
              <IconButton
                onClick={() => copyToClipboard(userData?.api_key)}
                sx={{
                  background: copied ? TEAL_LIGHT : '#F5F6F0', borderRadius: '10px',
                  '&:hover': { background: TEAL_LIGHT },
                }}
              >
                <CopyIcon sx={{ color: copied ? TEAL : TEXT_MUTED, fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {/* A disabled MUI button fires no events, so Tooltip needs a wrapper element
                to listen on — otherwise the tooltip dies while the key is regenerating. */}
            <Tooltip title="Regenerate API key">
              <span>
                <IconButton
                  onClick={() => setRegenKeyDialogOpen(true)}
                  disabled={regenKeyLoading}
                  sx={{
                    background: '#FFF3E0', borderRadius: '10px',
                    '&:hover': { background: '#FFE0B2' },
                  }}
                >
                  {regenKeyLoading
                    ? <CircularProgress size={18} />
                    : <RefreshIcon sx={{ color: ORANGE, fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box>
          <Typography sx={LABEL_SX}>Production API Key</Typography>
          <TextField
            fullWidth size="small"
            type={showApiKey ? 'text' : 'password'}
            value={userData?.api_key || ''}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '13px' },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showApiKey ? 'Hide key' : 'Show key'}>
                    <IconButton size="small" onClick={() => setShowApiKey(v => !v)} edge="end">
                      {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={INPUT_SX}
          />
          {copied && (
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEAL, mt: 0.75 }}>
              Copied to clipboard!
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Billing Address ───────────────────────────────────────────────
          Stripe collects and stores this now. It is asked for at checkout,
          where it is actually needed, instead of at signup, on the dashboard
          and in front of every purchase — and it is edited in Stripe's portal
          rather than here, so there is only ever one copy of it. */}
      <Box sx={CARD}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconBox bg={TEAL_LIGHT}>
              <HomeIcon sx={{ color: TEAL, fontSize: 24 }} />
            </IconBox>
            <Box>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 18, color: TEXT }}>
                Billing Address
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED }}>
                {hasAddress
                  ? 'Used on your invoices. Managed securely by Stripe.'
                  : "Stripe will ask for this at your next purchase. There's nothing to do now."}
              </Typography>
            </Box>
          </Box>
          {hasAddress && (
            <Box sx={{ bgcolor: '#E8F5E9', borderRadius: '9999px', px: 1.5, py: 0.5 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, color: '#1B5E20' }}>
                ON FILE
              </Typography>
            </Box>
          )}
        </Box>

        {addressError && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#C62828', mb: 1.5 }}>
            {addressError}
          </Typography>
        )}

        <Button
          variant="outlined"
          onClick={handleOpenBillingPortal}
          disabled={portalLoading}
          startIcon={portalLoading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600,
            border: `1px solid ${TEAL}`, borderRadius: '8px', color: TEAL,
          }}
        >
          {portalLoading ? 'Opening…' : 'Manage billing details'}
        </Button>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_MUTED, mt: 1.5 }}>
          Opens Stripe, where you can update your billing address, your cards and
          your invoice history. You'll come straight back here afterwards.
        </Typography>
      </Box>

      {/* ── Payment Methods ───────────────────────────────────────────────── */}
      <Box sx={CARD}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconBox bg={TEAL_LIGHT}>
              <PaymentIcon sx={{ color: TEAL, fontSize: 24 }} />
            </IconBox>
            <Box>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 18, color: TEXT }}>
                Payment Methods
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED }}>
                {/* 1.4 — OLD: "Manage cards used for subscription billing." */}
                Manage your cards used for subscription billing and manual credit purchases.
              </Typography>
            </Box>
          </Box>
          <Button
            onClick={handleAddCard}
            disabled={loading}
            startIcon={<AddIcon />}
            sx={{
              background: ORANGE_GRAD, color: '#fff', borderRadius: '10px',
              fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13,
              px: 2, py: 1, textTransform: 'none', boxShadow: 'none',
              '&:hover': { background: ORANGE_GRAD, opacity: 0.9, boxShadow: 'none' },
            }}
          >
            Add new
          </Button>
        </Box>

        {loading && paymentMethods.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: TEAL }} />
          </Box>
        ) : paymentMethods.length === 0 ? (
          <Box sx={{ background: '#F5F6F0', borderRadius: '12px', p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_MUTED }}>
              No payment methods added yet. Add a card to subscribe to paid plans.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paymentMethods.map((method) => (
              <Box
                key={method.id}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: '#F5F6F0', borderRadius: '12px', p: '16px 20px',
                  border: method.is_default ? `1.5px solid ${TEAL}` : '1.5px solid transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 44, height: 30, borderRadius: '6px', background: TEAL,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CardIcon sx={{ color: '#fff', fontSize: 18 }} />
                  </Box>
                  <Box>
                    {/* QA asked whether Apple Pay can be saved. It can — Stripe
                        saves the CARD behind the wallet, so the row is a normal
                        card and renewals charge it off-session. But the row read
                        "VISA •••• 4242" with nothing to show a wallet was used,
                        so there was no way to confirm it had worked. The tag
                        below names the wallet when there is one, and is absent
                        for a card that was keyed in directly. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14, color: TEXT }}>
                        {method.brand ? method.brand.toUpperCase() : 'CARD'} •••• {method.last4}
                      </Typography>
                      {WALLET_LABELS[method.wallet] && (
                        <Box
                          component="span"
                          sx={{
                            fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 10,
                            color: TEAL, background: TEAL_LIGHT, borderRadius: '6px', px: 0.75, py: '2px',
                          }}
                        >
                          {WALLET_LABELS[method.wallet]}
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_MUTED }}>
                      Expires {method.exp_month}/{method.exp_year}
                      {method.is_default && (
                        <Box component="span" sx={{ ml: 1, color: TEAL, fontWeight: 700 }}>● Default</Box>
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {!method.is_default && (
                    <Button
                      size="small"
                      onClick={() => handleSetDefaultCard(method.id)}
                      disabled={loading}
                      sx={{
                        fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEAL,
                        textTransform: 'none', border: `1px solid ${TEAL}`, borderRadius: '8px',
                        px: 1.5, py: 0.5, '&:hover': { background: TEAL_LIGHT },
                      }}
                    >
                      Set default
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveCard(method.id)}
                    disabled={loading}
                    sx={{ color: ORANGE, background: '#FDE8E4', borderRadius: '8px', '&:hover': { background: '#FCC8BB' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ── Danger Zone ───────────────────────────────────────────────────── */}
      <Box sx={{ ...CARD, mb: 0, border: '1px solid #FFD0C7', background: '#FFF8F7' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
          <IconBox bg="#FDE8E4">
            <WarningIcon sx={{ color: ORANGE, fontSize: 24 }} />
          </IconBox>
          <Box>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 18, color: ORANGE }}>
              Danger Zone
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED, mt: 0.5 }}>
              Deleting your account will permanently erase all your data, websites and API integrations.
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={() => setDeleteAccountDialogOpen(true)}
          sx={{
            background: '#FDE8E4', color: ORANGE, borderRadius: '10px',
            fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14,
            px: 3, py: 1.25, textTransform: 'none', border: '1px solid #FFB8A8',
            '&:hover': { background: '#FFCFC0' },
          }}
        >
          Delete account
        </Button>
      </Box>

      {/* ── Regenerate API Key Dialog ─────────────────────────────────────── */}
      <Dialog open={regenKeyDialogOpen} onClose={() => setRegenKeyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerate API Key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will generate a new API key and immediately invalidate the current one.
            All connected WordPress sites will need to be updated with the new key.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenKeyDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleRegenerateApiKey} variant="contained" color="warning" disabled={regenKeyLoading}>
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Remove Card Dialog ────────────────────────────────────────────── */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelRemoveCard} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Card?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this payment method? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemoveCard} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmRemoveCard} variant="contained" color="error" disabled={loading}>
            Remove card
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Account Dialog ─────────────────────────────────────────────
          ── WHAT THIS REPLACES, AND WHY ────────────────────────────────────
          There is no account-deletion endpoint — the feature is not built. The
          dialog nonetheless read "This action is permanent and cannot be undone.
          All your data, websites, and API integrations will be permanently
          deleted." above a red Delete Account button. Pressing it deleted
          nothing and opened a dialog titled ERROR.

          So the flow told someone they had just destroyed their account, then
          reported a failure, leaving the one question that matters unanswered:
          did any of it happen? Nothing had. Nothing could.

          The honest version says so BEFORE the commitment rather than after. It
          keeps the request possible — support really is the route today — but
          it no longer stages a destruction that cannot occur, and it is not an
          error, because nothing has gone wrong.

          When the endpoint lands, this becomes a real confirmation again and
          the mailto goes away. */}
      <Dialog open={deleteAccountDialogOpen} onClose={() => setDeleteAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: ORANGE }}>Deleting your account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Account deletion is handled by our support team, so that we can close
            your billing and remove your data completely.
            <br /><br />
            Email{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Delete my account')}`} sx={{ color: ORANGE, fontWeight: 700 }}>
              {SUPPORT_EMAIL}
            </Link>
            {' '}from the address on this account and we will confirm once it is done.
            <br /><br />
            Deletion is permanent: your websites, API integrations and any
            remaining credits go with the account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* No red "Delete Account" button. Nothing here deletes anything, and
              offering a destructive-looking control that cannot act is what
              produced the report. */}
          <Button onClick={() => setDeleteAccountDialogOpen(false)} color="inherit">Close</Button>
          <Button
            variant="contained"
            color="error"
            component="a"
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Delete my account')}`}
            onClick={() => setDeleteAccountDialogOpen(false)}
          >
            Email support
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Success Dialog ────────────────────────────────────────────────── */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>{successDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessDialogOpen(false)} variant="contained" color="primary">OK</Button>
        </DialogActions>
      </Dialog>

      {/* ── Error Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} variant="contained" color="primary">OK</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Settings;
